import React from 'react';
import PropTypes from 'prop-types';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/* ================= Conversión HTML a texto para PDF ================= */
function htmlToPdfLines(textOrHtml = '') {
  if (!textOrHtml || textOrHtml === '—') return ['—'];
  
  const root = document.createElement('div');
  const s = String(textOrHtml ?? '');
  
  const sanitized = s
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<style[\s\S]*?<\/style>/gi, '');
  
  root.innerHTML = sanitized;

  const lines = [];
  const push = (t = '') => { lines.push(t.replace(/\s+$/,'')); };
  const INDENT = (n) => '  '.repeat(n);
  
  const walk = (node, indent = 0) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const txt = node.nodeValue.replace(/\s+/g, ' ');
      if (txt.trim()) {
        if (!lines.length) push('');
        lines[lines.length - 1] += txt;
      }
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName.toLowerCase();

    if (tag === 'br') { push(''); return; }

    if (tag === 'p' || tag === 'div') {
      const before = lines.length;
      node.childNodes.forEach(ch => walk(ch, indent));
      if (lines.length === before) push('');
      push('');
      return;
    }

    if (tag === 'ul' || tag === 'ol') {
      let idx = 1;
      Array.from(node.children).forEach(li => {
        if (li.tagName && li.tagName.toLowerCase() === 'li') {
          const bullet = tag === 'ul' ? '• ' : `${idx}. `;
          const indentStr = INDENT(indent);
          
          // Obtener el contenido de texto del <li>
          const content = li.textContent?.trim() || '';
          
          // Agregar la línea completa con viñeta y contenido
          push(indentStr + bullet + content);
          
          idx += 1;
        }
      });
      push('');
      return;
    }

    if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') {
      push('');
      push('## ' + (node.textContent || '').trim());
      push('');
      return;
    }

    if (tag === 'strong' || tag === 'b') {
      if (!lines.length) push('');
      const content = node.textContent || '';
      lines[lines.length - 1] += content.toUpperCase(); // Usar mayúsculas en lugar de **
      return;
    }

    if (tag === 'em' || tag === 'i') {
      if (!lines.length) push('');
      node.childNodes.forEach(ch => walk(ch, indent));
      return;
    }

    if (tag === 'pre' || tag === 'code') {
      const text = (node.textContent || '').replace(/\r\n/g, '\n');
      push('');
      text.split('\n').forEach(ln => push(INDENT(indent) + '  ' + ln));
      push('');
      return;
    }

    if (tag === 'blockquote') {
      const text = node.textContent?.trim() || '';
      text.split('\n').forEach(line => {
        if (line.trim()) {
          push('│ ' + line.trim());
        }
      });
      push('');
      return;
    }

    node.childNodes.forEach(ch => walk(ch, indent));
  };

  walk(root, 0);
  
  // Limpiar líneas vacías al final
  while (lines.length && lines[lines.length - 1].trim() === '') lines.pop();
  
  // Limpiar líneas vacías duplicadas
  const cleaned = [];
  let prevEmpty = false;
  for (const line of lines) {
    const isEmpty = line.trim() === '';
    if (isEmpty && prevEmpty) continue;
    cleaned.push(line);
    prevEmpty = isEmpty;
  }
  
  return cleaned.length ? cleaned : ['—'];
}

/* ================= Defaults ================= */
function defaultFormatDate(d) {
  try {
    const x = d instanceof Date ? d : (d ? new Date(d) : null);
    return x && !isNaN(x) ? x.toLocaleString('es-CL', { timeZone: 'America/Santiago' }) : '';
  } catch { return ''; }
}

function defaultFmtCLP(n) {
  return new Intl.NumberFormat('es-CL', { style:'currency', currency:'CLP', maximumFractionDigits:0 }).format(Number(n||0));
}

function defaultGetUserName(l) { return l?.user_name || l?.member_name || l?.user || '—'; }
function defaultGetHours(l) { return Number(l?.hours ?? l?.hours_total ?? l?.hours_worked ?? 0); }
function defaultGetAmount(l) {
  const rate = Number(l?.hourly_rate_snapshot ?? l?.hourly_rate ?? 0);
  const hrs  = Number(l?.hours ?? 0);
  return Number(l?.amount_total ?? l?.amount ?? (rate * hrs) ?? 0);
}

// ===== Derivar "YYYY-MM-DD HH:mm–HH:mm" =====
const pad2 = (n) => String(n).padStart(2,'0');

function hhmm(str) {
  if (!str) return null;
  const m = String(str).match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = Number(m[1]); const mi = Number(m[2]);
  if (isNaN(h) || isNaN(mi)) return null;
  return `${pad2(h)}:${pad2(mi)}`;
}

function dateYYYYMMDD(str) {
  if (!str) return null;
  const m = String(str).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
}

function pickDateFromISO(iso) {
  return dateYYYYMMDD(iso);
}

function pickTimeFromISO(iso) {
  if (!iso) return null;
  const m = String(iso).match(/T(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : null;
}

function buildDateTimeRange(l) {
  const d0 = dateYYYYMMDD(l?.date);
  const s0 = hhmm(l?.start || l?.start_time);
  const e0 = hhmm(l?.end || l?.end_time);
  if (d0 && (s0 || e0)) {
    if (s0 && e0) return `${d0} ${s0}–${e0}`;
    if (s0)       return `${d0} ${s0}`;
    return `${d0} ${e0}`;
  }
  
  const ds = pickDateFromISO(l?.started_at || l?.startedAt);
  const de = pickDateFromISO(l?.ended_at || l?.endedAt);
  const s1 = pickTimeFromISO(l?.started_at || l?.startedAt);
  const e1 = pickTimeFromISO(l?.ended_at || l?.endedAt);
  const d1 = ds || de || d0;
  if (d1 && (s1 || e1)) {
    if (s1 && e1) return `${d1} ${s1}–${e1}`;
    if (s1)       return `${d1} ${s1}`;
    return `${d1} ${e1}`;
  }
  
  if (l?.date_display) return l.date_display;
  if (l?.date_obj instanceof Date && !isNaN(l.date_obj)) {
    return `${l.date_obj.getFullYear()}-${pad2(l.date_obj.getMonth()+1)}-${pad2(l.date_obj.getDate())}`;
  }
  if (typeof l?.date === 'string') {
    const d = dateYYYYMMDD(l.date);
    if (d) return d;
  }
  return '—';
}

export default function ExportProjectPDF({
  project,
  logs = [],
  formatDate = defaultFormatDate,
  getUserName = defaultGetUserName,
  getHours    = defaultGetHours,
  getAmount   = defaultGetAmount,
  fmtCLP      = defaultFmtCLP,
  filenamePrefix = 'Reporte',
  monthLabel,
  memberLabel,
  render,
  buttonProps = {},
}) {
  const onExport = React.useCallback(() => {
    if (!logs || logs.length === 0) {
      alert('No hay registros para exportar');
      return;
    }

    const doc = new jsPDF({ unit:'pt', format:'a4' });
    const margin = 50;
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - (margin * 2);
    const now = new Date();

    const primaryColor = [37, 99, 235];
    const secondaryColor = [100, 116, 139];
    const accentColor = [59, 130, 246];
    const lightBg = [248, 250, 252];

    const totalHoras = (logs || []).reduce((s,l)=> s + (getHours(l) || 0), 0);
    const totalMonto = (logs || []).reduce((s,l)=> s + (getAmount(l) || 0), 0);

    // ========== HEADER PRINCIPAL ==========
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 90, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text(`Reporte de Bitácora — ${project?.name || 'Proyecto'}`, margin, 35);
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Generado: ${formatDate(now)}`, margin, 55);
    doc.text(`Cliente: ${project?.client_name || '—'}`, margin, 70);

    let yPos = 110;

    // ========== INFORMACIÓN DE FILTROS Y TOTALES ==========
    doc.setFillColor(...lightBg);
    doc.rect(margin, yPos, contentWidth, 80, 'F');
    
    doc.setTextColor(...secondaryColor);
    doc.setFontSize(9);
    doc.setFont(undefined, 'bold');
    doc.text('FILTROS Y TOTALES', margin + 10, yPos + 20);
    
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    let filterText = '';
    if (monthLabel || memberLabel) {
      const monthPart = monthLabel ? `Mes: ${monthLabel}` : '';
      const memberPart = memberLabel ? `Miembro: ${memberLabel}` : 'Todos los miembros';
      filterText = [monthPart, memberPart].filter(Boolean).join(' · ');
    } else {
      filterText = 'Todos los registros';
    }
    doc.text(filterText, margin + 10, yPos + 40);
    
    doc.setFont(undefined, 'bold');
    doc.setTextColor(...accentColor);
    doc.text(`Total Horas: ${totalHoras.toFixed(2)} h`, margin + 10, yPos + 58);
    doc.text(`Total Monto: ${fmtCLP(totalMonto)}`, margin + 200, yPos + 58);

    yPos += 100;

    // ========== SEPARADOR ==========
    doc.setDrawColor(...accentColor);
    doc.setLineWidth(2);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 15;

    // ========== TABLA DE REGISTROS ==========
    doc.setTextColor(...primaryColor);
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text('Registros de Bitácora', margin, yPos);
    yPos += 20;

    const body = (logs || []).flatMap((l, index) => {
      const dateDisplay = buildDateTimeRange(l);
      const usr = getUserName(l) || '—';
      const hrs = (getHours(l) ?? 0).toFixed(2);
      const amt = fmtCLP(getAmount(l) ?? 0);
      const activity = l?.activity_code || '—';

      // Detectar si el contenido tiene HTML
      const rawNote = l?.progress_note || l?.note || l?.description || '';
      const hasHTML = /<[^>]+>/.test(rawNote);
      
      let avanceMultiline;
      if (hasHTML) {
        // Nuevo formato con HTML
        const avanceLines = htmlToPdfLines(rawNote);
        avanceMultiline = avanceLines.join('\n');
      } else {
        // Formato antiguo (texto plano)
        avanceMultiline = rawNote || '—';
      }

      return [
        [
          { content: dateDisplay, styles: { fontStyle: 'bold', textColor: secondaryColor } },
          usr,
          activity,
          { content: hrs, styles: { halign: 'right' } },
          { content: amt, styles: { halign: 'right' } }
        ],
        [
          { 
            content: avanceMultiline, 
            colSpan: 5, 
            styles: { 
              fillColor: index % 2 === 0 ? [255, 255, 255] : lightBg,
              textColor: [60, 60, 60],
              fontSize: 8,
              cellPadding: 8
            } 
          }
        ]
      ];
    });

    autoTable(doc, {
      startY: yPos,
      head: [[
        { content: 'Fecha', styles: { fontStyle: 'bold' } },
        { content: 'Miembro', styles: { fontStyle: 'bold' } },
        { content: 'Actividad', styles: { fontStyle: 'bold' } },
        { content: 'Horas', styles: { fontStyle: 'bold', halign: 'right' } },
        { content: 'Monto', styles: { fontStyle: 'bold', halign: 'right' } }
      ]],
      body,
      styles: { 
        fontSize: 9, 
        cellPadding: 6, 
        valign: 'top', 
        overflow: 'linebreak',
        lineColor: [220, 220, 220],
        lineWidth: 0.5
      },
      headStyles: { 
        fillColor: primaryColor,
        textColor: [255, 255, 255],
        fontSize: 10,
        fontStyle: 'bold',
        halign: 'left'
      },
      columnStyles: { 
        0: { cellWidth: 100 },
        1: { cellWidth: 90 },
        2: { cellWidth: 70 },
        3: { halign: 'right', cellWidth: 50 },
        4: { halign: 'right', cellWidth: 70 }
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255]
      },
      margin: { left: margin, right: margin }
    });

    // ========== FOOTER EN TODAS LAS PÁGINAS ==========
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const footerY = doc.internal.pageSize.getHeight() - 25;
      
      doc.setDrawColor(...lightBg);
      doc.setLineWidth(0.5);
      doc.line(margin, footerY - 10, pageWidth - margin, footerY - 10);
      
      doc.setTextColor(...secondaryColor);
      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text(`Página ${i} de ${totalPages}`, pageWidth - margin - 50, footerY);
      doc.text(`${project?.name || 'Proyecto'} · ${project?.client_name || ''}`, margin, footerY);
    }

    const safeName = (project?.name || 'Proyecto').replace(/[^\w\-]+/g,'_').slice(0,60);
    const yyyymmdd = now.toISOString().slice(0,10);
    const memberSlug = memberLabel && memberLabel !== 'Todos'
      ? `_${memberLabel.replace(/[^\w\-]+/g,'_').slice(0,40)}`
      : '';
    const monthSlug = monthLabel ? `_${monthLabel}` : '';
    doc.save(`${filenamePrefix}_${safeName}${monthSlug}${memberSlug}_${yyyymmdd}.pdf`);
  }, [project, logs, formatDate, getUserName, getHours, getAmount, fmtCLP, filenamePrefix, monthLabel, memberLabel]);

  if (typeof render === 'function') {
    return render(onExport);
  }
  return (
    <button className="btn" onClick={onExport} {...buttonProps}>
      PDF (Proyecto)
    </button>
  );
}

ExportProjectPDF.propTypes = {
  project: PropTypes.object,
  logs: PropTypes.array,
  formatDate: PropTypes.func,
  getUserName: PropTypes.func,
  getHours: PropTypes.func,
  getAmount: PropTypes.func,
  fmtCLP: PropTypes.func,
  filenamePrefix: PropTypes.string,
  monthLabel: PropTypes.string,
  memberLabel: PropTypes.string,
  render: PropTypes.func,
  buttonProps: PropTypes.object,
};